// Number, money and date formatting in the interface language.
//
// One function per LEGACY idiom in the codebase, so that with VITE_I18N off
// (and in English with it on) every call site renders byte for byte what it
// renders today, including the bare `toLocaleString()` calls that follow the
// browser's locale (kept on purpose; normalizing them to en-US is a separate
// post-flip item). Only the non-English languages use `Intl` with the active
// language. Prices stay USD in every language.
//
// Branch 3c moved the call sites onto these. A new number, money or date on a
// player-facing surface belongs here too, never inline.
import { activeLanguage } from './index'
import { i18nEnabled } from './flag'
import type { Lang } from './languages'

/** The BCP 47 tag Intl gets for a non-English interface language. */
const INTL_TAG: Record<Exclude<Lang, 'en'>, string> = {
  es: 'es',
  // Brazilian Portuguese (decided 2026-09-30).
  pt: 'pt-BR',
  ja: 'ja',
}

/**
 * What goes between two complete translated sentences shown as one run of
 * text. Japanese writes sentences back to back (the full stop "。" already
 * ends each one), so a space there reads as a stray gap.
 */
const SENTENCE_SEPARATOR: Record<Lang, string> = {
  en: ' ',
  es: ' ',
  pt: ' ',
  ja: '',
}

/**
 * Join complete, already translated sentences into one paragraph in the
 * active language. English (and every language with the flag off) joins with
 * a single space, byte for byte the `.join(' ')` it replaces. Only for whole
 * sentences: a space next to a name or a number inside a sentence belongs to
 * the catalog string, not here.
 */
export function joinSentences(parts: readonly string[]): string {
  return parts.join(sentenceSeparator())
}

/**
 * The separator joinSentences uses, for JSX where a translated sentence is
 * followed by another sentence or a link that reads as one (`{' '}` today).
 * ' ' in English, so a prerendered page is byte for byte unchanged.
 */
export function sentenceSeparator(): string {
  return SENTENCE_SEPARATOR[activeLanguage()]
}

/**
 * What goes between the items of a translated list ("3 combos, 1 tutor").
 * Japanese lists with the ideographic comma and no space.
 */
const LIST_SEPARATOR: Record<Lang, string> = {
  en: ', ',
  es: ', ',
  pt: ', ',
  ja: '、',
}

/**
 * Join already translated list items in the active language. English (and
 * every language with the flag off) is byte for byte the `.join(', ')` it
 * replaces. Not for card names, which stay English and keep ", ".
 */
export function joinList(parts: readonly string[]): string {
  return parts.join(LIST_SEPARATOR[activeLanguage()])
}

/**
 * What ends a translated sentence that code, not the catalog, closes before
 * the next one: ". " in English, "。" in Japanese.
 */
const SENTENCE_END: Record<Lang, string> = {
  en: '. ',
  es: '. ',
  pt: '. ',
  ja: '。',
}

/** The code-side full stop plus separator for the active language. */
export function sentenceEnd(): string {
  return SENTENCE_END[activeLanguage()]
}

/** The Intl tag when localized formatting applies, else null (legacy path). */
function localizedTag(): string | null {
  if (!i18nEnabled()) return null
  const lang = activeLanguage()
  return lang === 'en' ? null : INTL_TAG[lang]
}

/** Legacy: `$${n.toFixed(2)}`. Localized: Intl USD currency. */
export function formatUsd(n: number): string {
  const tag = localizedTag()
  if (!tag) return `$${n.toFixed(2)}`
  // eslint-disable-next-line i18next/no-literal-string -- Intl option, or the flag-off English relative time (byte-identical, 3c)
  return new Intl.NumberFormat(tag, { style: 'currency', currency: 'USD' }).format(n)
}

/**
 * A USD amount short enough for a ~32px card thumbnail strip: two decimals
 * under $0.10, one under $10, whole dollars under $1,000, compact above
 * ("$0.05", "$1.2", "$12", "$1.2K"). Rounded, never cut: the full amount
 * belongs in the accessible label. Localized: the same rules with Intl in the
 * interface language and the narrow "$" symbol ("1,2 $"), not "US$".
 */
export function formatUsdCompact(n: number): string {
  const digits = n < 0.1 ? 2 : n < 10 ? 1 : 0
  const tag = localizedTag()
  if (!tag && n < 1000) return `$${n.toFixed(digits)}`
  // eslint-disable-next-line i18next/no-literal-string -- Intl options
  return new Intl.NumberFormat(tag ?? 'en-US', {
    // eslint-disable-next-line i18next/no-literal-string -- Intl options
    style: 'currency', currency: 'USD', currencyDisplay: 'narrowSymbol',
    // eslint-disable-next-line i18next/no-literal-string -- Intl options
    notation: n < 1000 ? 'standard' : 'compact',
    minimumFractionDigits: n < 1000 ? digits : 0,
    maximumFractionDigits: n < 1000 ? digits : 1,
  }).format(n)
}

/**
 * Legacy: `n.toLocaleString('en-US', { style: 'currency', currency: 'USD' })`,
 * which groups thousands ("$1,234.50"), unlike formatUsd. Localized: the same
 * Intl USD currency as formatUsd.
 */
export function formatUsdEnUs(n: number): string {
  // eslint-disable-next-line i18next/no-literal-string -- Intl option, or the flag-off English relative time (byte-identical, 3c)
  const opts: Intl.NumberFormatOptions = { style: 'currency', currency: 'USD' }
  // eslint-disable-next-line i18next/no-literal-string -- Intl option, or the flag-off English relative time (byte-identical, 3c)
  return n.toLocaleString(localizedTag() ?? 'en-US', opts)
}

// A safe plain decimal as a person types it: 1 to 12 integer digits with no
// leading zero (so Intl cannot drop one), then at most two decimals. Twelve
// digits is far inside Number's exact-integer range, so Number(raw) never
// changes the amount the person entered.
const TYPED_AMOUNT = /^(0|[1-9]\d{0,11})(?:\.(\d{1,2}))?$/

/**
 * Legacy: `` `$${raw}` `` on the raw text of an amount input, so an in-progress
 * value renders exactly as typed. Localized: Intl USD with the typed number of
 * decimals and no thousands grouping (still "as typed"), ONLY for a safe plain
 * non-negative decimal (TYPED_AMOUNT). Anything else - half-typed ("12."),
 * negative, more than two decimals, more than twelve digits, exponent or
 * other odd forms - renders exactly as the legacy path did, in every language.
 */
export function formatUsdTyped(raw: string): string {
  const tag = localizedTag()
  const m = tag ? TYPED_AMOUNT.exec(raw) : null
  if (!tag || !m) return `$${raw}`
  const digits = m[2]?.length ?? 0
  return new Intl.NumberFormat(tag, {
    // eslint-disable-next-line i18next/no-literal-string -- Intl option, or the flag-off English relative time (byte-identical, 3c)
    style: 'currency',
    // eslint-disable-next-line i18next/no-literal-string -- Intl option, or the flag-off English relative time (byte-identical, 3c)
    currency: 'USD',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    useGrouping: false,
  }).format(Number(raw))
}

/** Legacy: `n.toLocaleString('en-US')`. */
export function formatCountEnUs(n: number): string {
  const tag = localizedTag()
  // eslint-disable-next-line i18next/no-literal-string -- Intl option, or the flag-off English relative time (byte-identical, 3c)
  return n.toLocaleString(tag ?? 'en-US')
}

/** Legacy: bare `n.toLocaleString()`, which follows the browser's locale. */
export function formatCountBrowser(n: number): string {
  const tag = localizedTag()
  return tag ? n.toLocaleString(tag) : n.toLocaleString()
}

/** Legacy: `d.toLocaleDateString(undefined, opts)` (the browser's locale). */
export function formatDateBrowser(d: Date, opts?: Intl.DateTimeFormatOptions): string {
  return d.toLocaleDateString(localizedTag() ?? undefined, opts)
}

/** Legacy: `d.toLocaleTimeString(undefined, opts)` (the browser's locale). */
export function formatTimeBrowser(d: Date, opts?: Intl.DateTimeFormatOptions): string {
  return d.toLocaleTimeString(localizedTag() ?? undefined, opts)
}

/**
 * Legacy: `new Intl.DateTimeFormat('en-US', opts).format(d)`, which is also
 * `d.toLocaleDateString('en-US', opts)` whenever `opts` names a date field.
 */
export function formatDateEnUs(d: Date, opts?: Intl.DateTimeFormatOptions): string {
  // eslint-disable-next-line i18next/no-literal-string -- Intl option, or the flag-off English relative time (byte-identical, 3c)
  return new Intl.DateTimeFormat(localizedTag() ?? 'en-US', opts).format(d)
}

function formatDecimal(n: number, digits: number): string {
  const tag = localizedTag()
  if (!tag) return n.toFixed(digits)
  // toFixed never groups thousands, so neither does the localized form.
  return new Intl.NumberFormat(tag, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    useGrouping: false,
  }).format(n)
}

/** Legacy: `n.toFixed(1)`. */
export function formatDecimal1(n: number): string {
  return formatDecimal(n, 1)
}

/** Legacy: `n.toFixed(2)`. */
export function formatDecimal2(n: number): string {
  return formatDecimal(n, 2)
}

/**
 * How long ago an ISO timestamp was. Legacy strings are exactly
 * ChatHistory's: '' for an unparseable date, then 'just now', 'Nm ago',
 * 'Nh ago', 'Nd ago'. Localized: Intl.RelativeTimeFormat on the same
 * rounding.
 */
export function formatRelative(iso: string, now: number = Date.now()): string {
  const then = Date.parse(iso)
  if (Number.isNaN(then)) return ''
  const mins = Math.max(0, Math.round((now - then) / 60000))
  const tag = localizedTag()
  if (!tag) {
    // eslint-disable-next-line i18next/no-literal-string -- Intl option, or the flag-off English relative time (byte-identical, 3c)
    if (mins < 1) return 'just now'
    // eslint-disable-next-line i18next/no-literal-string -- Intl option, or the flag-off English relative time (byte-identical, 3c)
    if (mins < 60) return `${mins}m ago`
    const hours = Math.round(mins / 60)
    // eslint-disable-next-line i18next/no-literal-string -- Intl option, or the flag-off English relative time (byte-identical, 3c)
    if (hours < 24) return `${hours}h ago`
    // eslint-disable-next-line i18next/no-literal-string -- Intl option, or the flag-off English relative time (byte-identical, 3c)
    return `${Math.round(hours / 24)}d ago`
  }
  // eslint-disable-next-line i18next/no-literal-string -- Intl option, or the flag-off English relative time (byte-identical, 3c)
  const rtf = new Intl.RelativeTimeFormat(tag, { numeric: 'auto' })
  // eslint-disable-next-line i18next/no-literal-string -- Intl option, or the flag-off English relative time (byte-identical, 3c)
  if (mins < 1) return rtf.format(0, 'second')
  // eslint-disable-next-line i18next/no-literal-string -- Intl option, or the flag-off English relative time (byte-identical, 3c)
  if (mins < 60) return rtf.format(-mins, 'minute')
  const hours = Math.round(mins / 60)
  // eslint-disable-next-line i18next/no-literal-string -- Intl option, or the flag-off English relative time (byte-identical, 3c)
  if (hours < 24) return rtf.format(-hours, 'hour')
  // eslint-disable-next-line i18next/no-literal-string -- Intl option, or the flag-off English relative time (byte-identical, 3c)
  return rtf.format(-Math.round(hours / 24), 'day')
}
